import type { CondoRole, Permission } from '../../../lib/api/condo';

/** Roles con vista web. guard y maintenance solo existen en movil. */
export const WEB_ROLES = ['admin', 'board', 'owner', 'resident'] as const;
export type WebRole = (typeof WEB_ROLES)[number];

export function isWebRole(role: CondoRole | string): role is WebRole {
  return (WEB_ROLES as readonly string[]).includes(role);
}

export function webRoles(roles: readonly (CondoRole | string)[] | undefined): WebRole[] {
  return WEB_ROLES.filter((r) => roles?.includes(r));
}

/** Rol por defecto: el de mayor alcance. */
export function pickDefaultRole(roles: readonly (CondoRole | string)[] | undefined, preferred?: string | null): WebRole | null {
  const available = webRoles(roles);
  if (preferred && available.includes(preferred as WebRole)) return preferred as WebRole;
  return available[0] ?? null;
}

/**
 * Permisos que muestra la UI: los efectivos (union de roles, mandan) filtrados por
 * by_role[rolActivo]. Nunca agrega permisos. Niega por defecto: sin rol activo o si by_role
 * no trae el rol activo devuelve [] (sin respaldo a otro rol). El backend revalida cada request.
 */
export function uiPermissions(
  effective: readonly Permission[] | undefined,
  byRole: Partial<Record<string, readonly Permission[]>> | undefined,
  activeRole: string | null | undefined,
): Permission[] {
  const scope = activeRole ? byRole?.[activeRole] : undefined;
  if (!scope) return [];
  return (effective ?? []).filter((p) => scope.includes(p));
}

/** Solo para UI: el backend vuelve a validar cada request. */
export function hasPermissions(
  granted: readonly Permission[] | undefined,
  required: Permission | readonly Permission[],
  mode: 'all' | 'any' = 'all',
): boolean {
  const list = (Array.isArray(required) ? required : [required]) as readonly Permission[];
  if (list.length === 0) return true;
  if (!granted?.length) return false;
  return mode === 'all' ? list.every((p) => granted.includes(p)) : list.some((p) => granted.includes(p));
}
